// src/components/mitchRoom/modelOutputs/TeamPredictionChart.js
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const COLORS = {
  Win: '#59A14F',
  Draw: '#BAB0AC',
  Loss: '#E15759',
};

const TEXT_COLORS = {
  Win: 'text-success',
  Draw: 'text-muted-foreground',
  Loss: 'text-destructive',
};

// Get match result from scores
function getMatchResult(clubScore, oppScore) {
  if (clubScore == null || oppScore == null) return null;
  if (clubScore > oppScore) return 'Win';
  if (clubScore < oppScore) return 'Loss';
  return 'Draw';
}

export default function TeamPredictionChart({ teamPredictions }) {
  // Filter to historical predictions only - must have scores
  const historicalPredictions = useMemo(() => {
    if (!teamPredictions) return [];
    return teamPredictions.filter((p) => {
      // Historical = has scores (club_score and opp_club_score both not null)
      const hasScores = p.club_score != null && p.opp_club_score != null;
      return hasScores;
    });
  }, [teamPredictions]);

  // Transform data into 5% buckets with counts per result
  const chartData = useMemo(() => {
    // Initialize buckets from 0 to 100 in 5% increments
    const buckets = {};
    for (let i = 0; i <= 100; i += 5) {
      buckets[i] = { bucket: i, Win: 0, Draw: 0, Loss: 0 };
    }

    // Populate buckets
    historicalPredictions.forEach((result) => {
      const matchResult = getMatchResult(result.club_score, result.opp_club_score);
      if (!matchResult) return;

      const percentWin = parseFloat(result.percent_win) || 0;
      const roundedBucket = Math.round(percentWin / 5) * 5;
      // Clamp to valid range
      const bucket = Math.max(0, Math.min(100, roundedBucket));

      if (buckets[bucket] && buckets[bucket][matchResult] !== undefined) {
        buckets[bucket][matchResult]++;
      }
    });

    // Filter out empty buckets and 0% bucket (bad predictions)
    return Object.values(buckets).filter(
      (b) => b.bucket > 0 && (b.Win > 0 || b.Draw > 0 || b.Loss > 0)
    );
  }, [historicalPredictions]);

  // Calculate stats (count, mean, median) for each result type
  const stats = useMemo(() => {
    const groups = { Win: [], Draw: [], Loss: [] };

    historicalPredictions.forEach((result) => {
      const matchResult = getMatchResult(result.club_score, result.opp_club_score);
      if (!matchResult) return;

      const percentWin = parseFloat(result.percent_win) || 0;
      // Exclude 0% predictions (bad data)
      if (percentWin > 0 && groups[matchResult]) {
        groups[matchResult].push(percentWin);
      }
    });

    return Object.entries(groups).map(([result, values]) => {
      if (values.length === 0) {
        return { result, count: 0, mean: null, median: null };
      }

      const sorted = [...values].sort((a, b) => a - b);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const median = sorted[Math.floor(sorted.length / 2)];

      return { result, count: values.length, mean, median };
    });
  }, [historicalPredictions]);

  // Headline calibration metrics for the win-probability model:
  // Brier score = mean squared error of predicted win probability vs outcome.
  // ECE = weighted mean absolute gap between predicted win % and actual win
  // rate per 5% bucket. Both computed client-side in a single pass.
  const calibrationMetrics = useMemo(() => {
    let n = 0;
    let brierSum = 0;
    const buckets = new Map(); // bucket -> { sumProb, wins, count }

    historicalPredictions.forEach((result) => {
      const matchResult = getMatchResult(result.club_score, result.opp_club_score);
      if (!matchResult) return;

      const percentWin = parseFloat(result.percent_win) || 0;
      if (percentWin <= 0) return; // Exclude 0% predictions (bad data), matching chart/stats

      const prob = percentWin / 100;
      const won = matchResult === 'Win' ? 1 : 0;
      brierSum += (prob - won) ** 2;
      n++;

      const bucket = Math.max(0, Math.min(100, Math.round(percentWin / 5) * 5));
      const entry = buckets.get(bucket) || { sumProb: 0, wins: 0, count: 0 };
      entry.sumProb += prob;
      entry.wins += won;
      entry.count++;
      buckets.set(bucket, entry);
    });

    if (n === 0) return null;

    let ece = 0;
    buckets.forEach(({ sumProb, wins, count }) => {
      ece += Math.abs(wins / count - sumProb / count) * (count / n);
    });

    return { brier: brierSum / n, ece, n };
  }, [historicalPredictions]);

  const totalMatches = stats.reduce((acc, s) => acc + s.count, 0);

  if (totalMatches === 0) {
    return (
      <div className="p-6 text-center text-token-md text-muted-foreground">
        No historical team prediction data available.
      </div>
    );
  }

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <h3 className="text-token-xl font-semibold">
        Model Calibration: Win Probability vs Actual Results
      </h3>
      <p className="mb-3 text-token-base text-muted-foreground">
        Shows how predicted win probabilities correlate with actual match outcomes. Ideally, at
        higher win probabilities, more results should be wins (green).
      </p>

      {/* Headline calibration metrics */}
      {calibrationMetrics && (
        <div className="mb-3 flex flex-wrap gap-3">
          <div className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
            <p className="text-token-base text-muted-foreground">Brier Score</p>
            <p className="text-token-2xl font-semibold">{calibrationMetrics.brier.toFixed(3)}</p>
            <p className="text-token-xs text-muted-foreground">Lower is better (0 = perfect)</p>
          </div>
          <div className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
            <p className="text-token-base text-muted-foreground">ECE</p>
            <p className="text-token-2xl font-semibold">
              {(calibrationMetrics.ece * 100).toFixed(1)}%
            </p>
            <p className="text-token-xs text-muted-foreground">
              Expected calibration error over {calibrationMetrics.n.toLocaleString()} matches
            </p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="mb-4 flex flex-wrap gap-3">
        {stats.map((stat) => (
          <div key={stat.result} className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
            <p className={`text-token-md font-semibold ${TEXT_COLORS[stat.result]}`}>
              {stat.result}
            </p>
            <p className="text-token-sm">
              Count: {stat.count} ({((stat.count / totalMatches) * 100).toFixed(1)}%)
            </p>
            {stat.count > 0 && stat.mean != null && stat.median != null && (
              <>
                <p className="text-token-sm">Mean: {stat.mean.toFixed(1)}%</p>
                <p className="text-token-sm">Median: {stat.median.toFixed(1)}%</p>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Stacked Bar Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 40 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(value) => `${value}%`}
            label={{ value: 'Predicted Win %', position: 'insideBottom', offset: -10 }}
          />
          <YAxis
            label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(value, name) => [value, name]}
            labelFormatter={(label) => `Win Probability: ${label}%`}
          />
          <Bar dataKey="Win" stackId="a" fill={COLORS.Win} name="Win" isAnimationActive={false} />
          <Bar dataKey="Draw" stackId="a" fill={COLORS.Draw} name="Draw" isAnimationActive={false} />
          <Bar dataKey="Loss" stackId="a" fill={COLORS.Loss} name="Loss" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
